import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, PlayCircle, CheckCircle2, Lock } from "lucide-react";
import { api, API, errMsg } from "../lib/api";
import { PartCheck } from "./PartCheck";

const mediaUrl = (path) => `${API}/media/${path}`;
const posKey = (id) => `dx-pos-${id}`;
const savePos = (id, el) => { if (el.currentTime > 5 && !el.ended) localStorage.setItem(posKey(id), String(Math.floor(el.currentTime))); };
const restorePos = (id, el) => { const t = Number(localStorage.getItem(posKey(id))); if (t && t < el.duration - 5) el.currentTime = t; };

const SessionQuiz = ({ slug, mod, onPassed }) => {
  const [answers, setAnswers] = useState(Array(mod.quiz.length).fill(-1));
  const [result, setResult] = useState(null);
  const submit = async () => {
    try {
      const { data } = await api.post(`/learn/${slug}/modules/${mod.id}/session-quiz`, { answers });
      setResult(data);
      if (data.passed) { toast.success(`Session quiz passed - ${data.score}%`); onPassed(); } else toast.error(`${data.score}% - you need ${data.pass_mark}%. Rewatch and try again.`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mt-8 border-t-2 border-[#D4AF37] pt-6" data-testid="session-quiz">
      <div className="dx-eyebrow">Session quiz</div>
      <p className="text-sm text-[#6b5a73] mt-1">Check your understanding before moving on.</p>
      {result && !result.passed && <div className="mt-3 rounded-md bg-rose-50 text-rose-700 text-sm p-3" data-testid="session-quiz-failed">You scored {result.score}% ({result.correct}/{result.total}).</div>}
      <div className="space-y-5 mt-4">
        {mod.quiz.map((q, i) => (
          <div key={i}>
            <div className="font-semibold text-sm">{i + 1}. {q.q}</div>
            <div className="mt-2 grid sm:grid-cols-2 gap-2">
              {q.options.map((o, j) => (
                <button key={j} onClick={() => setAnswers(answers.map((a, k) => (k === i ? j : a)))} data-testid={`sq-q${i}-opt${j}`}
                  className={`text-left rounded-md border px-3 py-2 text-sm ${answers[i] === j ? "border-[#5B2A6E] bg-[#F4EEF7] font-semibold" : "hover:bg-[#FAF8FC]"}`}>{o}</button>
              ))}
            </div>
          </div>
        ))}
      </div>
      <button className="dx-btn-gold mt-5" disabled={answers.includes(-1)} onClick={submit} data-testid="session-quiz-submit-btn">Submit session quiz</button>
    </div>
  );
};

export default function ModuleBody({ slug, mod, done, onComplete, onPassed, initialPart = 0, preview = false }) {
  const [part, setPart] = useState(initialPart);
  const [prog, setProg] = useState({});
  const [asking, setAsking] = useState(false);
  const loadProg = () => api.get(`/learn/${slug}/progress`).then((r) => setProg(Object.fromEntries(r.data.map((p) => [p.part_id, p]))));
  useEffect(() => { setPart(Math.min(initialPart, Math.max((mod.parts || []).length - 1, 0))); setAsking(false); }, [mod.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadProg(); }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps
  const parts = mod.parts || [];
  const isDone = done.includes(mod.id);
  const cleared = (p) => prog[p.id]?.watched && (!p.check || prog[p.id]?.answered);
  const unlocked = (i) => preview || i === 0 || cleared(parts[i - 1]);
  const ended = async () => {
    const p = parts[part];
    localStorage.removeItem(posKey(p.id));
    if (!p.check) {
      await api.post(`/learn/${slug}/modules/${mod.id}/parts/${p.id}/watched`).catch(() => {});
      await loadProg();
      if (part < parts.length - 1) setPart(part + 1);
    } else if (prog[p.id]?.answered) {
      if (part < parts.length - 1) setPart(part + 1);
    } else setAsking(true);
  };
  const afterCheck = async () => {
    setAsking(false);
    await loadProg();
    if (part < parts.length - 1) setPart(part + 1);
  };
  return (
    <>
      {parts.length > 0 && (
        <div className="mt-5" data-testid="learn-module-video">
          <div className="relative">
            <video key={parts[part].id} src={mediaUrl(parts[part].path)} controls preload="metadata" playsInline className="w-full aspect-video bg-black rounded-md" onEnded={ended}
              onLoadedMetadata={(e) => restorePos(parts[part].id, e.currentTarget)} onPause={(e) => savePos(parts[part].id, e.currentTarget)}
              onTimeUpdate={(e) => Math.floor(e.currentTarget.currentTime) % 10 === 0 && savePos(parts[part].id, e.currentTarget)} />
            {asking && <PartCheck key={parts[part].id} slug={slug} modId={mod.id} part={parts[part]} isLast={part === parts.length - 1} onContinue={afterCheck} />}
          </div>
          <div className="flex flex-wrap gap-2 mt-3" data-testid="learn-parts">
            {parts.map((p, i) => (
              <button key={p.id} disabled={!unlocked(i)} onClick={() => { setAsking(false); setPart(i); }} data-testid={`learn-part-${i}`}
                title={unlocked(i) ? "" : "Finish the previous part and its question first"}
                className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold border disabled:opacity-40 disabled:cursor-not-allowed ${i === part ? "bg-[#5B2A6E] text-white border-[#5B2A6E]" : "text-[#5B2A6E] hover:bg-[#F4EEF7]"}`}>
                {cleared(p) ? <CheckCircle2 className="w-3.5 h-3.5 text-[#D4AF37]" /> : unlocked(i) ? <PlayCircle className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}{p.title}
              </button>
            ))}
          </div>
        </div>
      )}
      {mod.slides && <a href={mediaUrl(mod.slides.path)} target="_blank" rel="noreferrer" className="dx-btn-ghost mt-4" data-testid="learn-module-slides"><FileText className="w-4 h-4" />Session slides (PDF)</a>}
      <div className="mt-5 text-[#3b2f42] leading-relaxed whitespace-pre-line">{mod.content}</div>
      {mod.quiz?.length > 0 && !isDone && <SessionQuiz key={mod.id} slug={slug} mod={mod} onPassed={onPassed} />}
      {isDone && mod.quiz?.length > 0 && <div className="mt-6 flex items-center gap-2 text-emerald-700 text-sm font-semibold" data-testid="session-quiz-passed"><CheckCircle2 className="w-4 h-4" />Session quiz passed</div>}
      {(!mod.quiz?.length || isDone) && (
        <button className="dx-btn-purple mt-8" onClick={onComplete} data-testid="learn-complete-module-btn">{isDone ? "Next" : "Mark as complete"}</button>
      )}
    </>
  );
}
